import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    Link,
    useColorModeValue,
    Divider,
    List,
    ListItem,
    ListIcon,
    Icon,
} from '@chakra-ui/react';
import { FiCheck, FiArrowLeft, FiShield } from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';

function PrivacyPolicy() {
    const textColor = useColorModeValue('gray.700', 'white');
    const bgColor = useColorModeValue('white', 'gray.800');
    const brandColor = useColorModeValue('brand.500', 'brand.400');
    const borderColor = useColorModeValue('gray.200', 'gray.600');

    return (
        <Box minH="100vh" bg={bgColor}>
            <Box
                bgImage="linear-gradient(to right, rgba(255, 26, 61, 0.85), rgba(255, 94, 0, 0.85)), url('/assets/img/food.jpg')"
                bgSize="cover"
                bgPosition="center"
                height="30vh"
                position="relative"
                boxShadow="xl"
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                <VStack spacing={4}>
                    <Icon as={FiShield as any} color="white" boxSize={8} />
                    <Heading color="white" fontSize={{ base: "3xl", md: "4xl" }} fontWeight="bold">
                        Privacy Policy
                    </Heading>
                    <Text color="white" fontSize={{ base: "md", md: "lg" }} textAlign="center" maxW="md">
                        How we collect, use, and protect your information
                    </Text>
                </VStack>
            </Box>

            <Container maxW="container.md" py={6}>
                <Link as={RouterLink} to="/sign-in" color={brandColor} display="inline-flex" alignItems="center" mb={6}>
                    <Icon as={FiArrowLeft as any} style={{ marginRight: '8px' }} color={brandColor} />
                    Back to Sign In
                </Link>
            </Container>

            <Container maxW="container.md" py={8}>
                <VStack spacing={8} align="stretch">
                    <Box>
                        <Text color="gray.500" fontSize="sm" mb={4}>
                            Last updated: {new Date().toLocaleDateString()}
                        </Text>

                        <Text color={textColor} fontSize="md" lineHeight="tall">
                            This Privacy Policy describes how our Restaurant Management Platform collects, uses, and protects your personal information when you use our service. We are committed to protecting your privacy and ensuring the security of your data.
                        </Text>
                    </Box>

                    <Divider borderColor={borderColor} />

                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            1. Information We Collect
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall" mb={4}>
                            We collect several types of information to provide and improve our services:
                        </Text>

                        <Heading size="sm" color={textColor} mb={3}>
                            Personal Information
                        </Heading>
                        <List spacing={2} mb={4}>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Name and contact information (email, phone)</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Business information (restaurant name, address)</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Payment and billing information</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Account credentials and preferences</Text>
                            </ListItem>
                        </List>

                        <Heading size="sm" color={textColor} mb={3}>
                            Usage Information
                        </Heading>
                        <List spacing={2}>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Order and transaction data</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Menu and inventory information</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Customer feedback and reviews</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Analytics and performance metrics</Text>
                            </ListItem>
                        </List>
                    </Box>

                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            2. How We Use Your Information
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall" mb={4}>
                            We use the collected information for the following purposes:
                        </Text>
                        <List spacing={2}>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Provide and maintain our services</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Process orders and payments</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Send notifications and updates</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Improve our platform and services</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Provide customer support</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Comply with legal obligations</Text>
                            </ListItem>
                        </List>
                    </Box>

                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            3. Information Sharing and Disclosure
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall" mb={4}>
                            We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
                        </Text>
                        <List spacing={2}>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="blue.500" />
                                <Text color={textColor} fontSize="md">With your explicit consent</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="blue.500" />
                                <Text color={textColor} fontSize="md">To comply with legal requirements</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="blue.500" />
                                <Text color={textColor} fontSize="md">With service providers who assist in our operations</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="blue.500" />
                                <Text color={textColor} fontSize="md">To protect our rights and prevent fraud</Text>
                            </ListItem>
                        </List>
                    </Box>

                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            4. Data Security
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall" mb={4}>
                            We implement appropriate security measures to protect your personal information:
                        </Text>
                        <List spacing={2}>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Encryption of data in transit and at rest</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Regular security assessments and updates</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Access controls and authentication</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Secure data centers and infrastructure</Text>
                            </ListItem>
                        </List>
                    </Box>

                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            5. Your Rights and Choices
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall" mb={4}>
                            You have the following rights regarding your personal information:
                        </Text>
                        <List spacing={2}>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Access and review your personal data</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Update or correct inaccurate information</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Request deletion of your data</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Opt-out of marketing communications</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="green.500" />
                                <Text color={textColor} fontSize="md">Export your data in a portable format</Text>
                            </ListItem>
                        </List>
                    </Box>

                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            6. Cookies and Tracking Technologies
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall" mb={4}>
                            We use cookies and similar technologies to enhance your experience:
                        </Text>
                        <List spacing={2}>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="blue.500" />
                                <Text color={textColor} fontSize="md">Essential cookies for platform functionality</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="blue.500" />
                                <Text color={textColor} fontSize="md">Analytics cookies to improve our services</Text>
                            </ListItem>
                            <ListItem display="flex" alignItems="center">
                                <ListIcon as={FiCheck as any} color="blue.500" />
                                <Text color={textColor} fontSize="md">Preference cookies to remember your settings</Text>
                            </ListItem>
                        </List>
                        <Text color={textColor} fontSize="md" lineHeight="tall" mt={4}>
                            You can control cookie settings through your browser preferences.
                        </Text>
                    </Box>

                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            7. Data Retention
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall">
                            We retain your personal information for as long as necessary to provide our services and comply with legal obligations. When we no longer need your information, we will securely delete or anonymize it.
                        </Text>
                    </Box>

                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            8. International Data Transfers
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall">
                            Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.
                        </Text>
                    </Box>

                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            9. Children&apos;s Privacy
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall">
                            Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have collected such information, please contact us immediately.
                        </Text>
                    </Box>

                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            10. Changes to This Privacy Policy
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall">
                            We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the &quot;Last updated&quot; date.
                        </Text>
                    </Box>

                    <Box>
                        <Heading size="md" color={textColor} mb={4}>
                            11. Contact Us
                        </Heading>
                        <Text color={textColor} fontSize="md" lineHeight="tall" mb={4}>
                            If you have any questions about this Privacy Policy or our data practices, please contact us:
                        </Text>
                        <VStack align="start" spacing={2}>
                            <Text color={textColor} fontSize="md">
                                Email: magauuapp@gmail.com
                            </Text>
                            <Text color={textColor} fontSize="md">
                                Phone: +9779860363646
                            </Text>
                            {/* <Text color={textColor} fontSize="md">
                                Address: 123 Business Street, Suite 100, City, State 12345
                            </Text> */}
                        </VStack>
                    </Box>

                    <Divider borderColor={borderColor} />

                    <Box textAlign="center">
                        <Text color="gray.500" fontSize="sm">
                            By using our service, you acknowledge that you have read and understood this Privacy Policy and consent to the collection and use of your information as described herein.
                        </Text>
                    </Box>
                </VStack>
            </Container>
        </Box>
    );
}

export default PrivacyPolicy; 